import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, uploadFile } from "../api";
import { useT } from "../i18n";
import { tg } from "../telegram";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { downloadExport, fetchBlobUrl } from "../download";
import ShareSheet, { type ShareSpec } from "../components/ShareSheet";
import type { FileItem, Me } from "../types";

function humanSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fileIcon(f: FileItem): string {
  if (f.kind === "image") return "🖼";
  const ct = f.content_type || "";
  if (ct.includes("pdf")) return "📕";
  if (ct.includes("word") || ct.includes("wordprocessing")) return "📄";
  if (ct.includes("sheet") || ct.includes("excel")) return "📊";
  if (ct.includes("presentation") || ct.includes("powerpoint")) return "📑";
  return "📎";
}

export default function FolderView({ me }: { me: Me }) {
  const { folderId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const isAdmin = me.role === "admin";
  const [files, setFiles] = useState<FileItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [preview, setPreview] = useState<{ file: FileItem; url: string } | null>(null);
  const [shareSpec, setShareSpec] = useState<ShareSpec | null>(null);

  const load = () =>
    api
      .get<FileItem[]>(`/api/folders/${folderId}/files`)
      .then(setFiles)
      .catch((e: ApiError) => setError(e.message));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  const closePreview = useCallback(() => {
    setPreview((p) => {
      if (p) URL.revokeObjectURL(p.url);
      return null;
    });
  }, []);

  const onBack = useCallback(() => {
    if (preview) {
      closePreview();
      return;
    }
    navigate("/files");
  }, [preview, closePreview, navigate]);
  useTelegramBackButton(onBack);

  const alert_ = (m: string) => {
    const a = tg()?.showAlert;
    if (a) a(m);
    else window.alert(m);
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || uploading) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setUploading(true);
    try {
      await uploadFile(`/api/folders/${folderId}/files`, file);
      load();
    } catch (err) {
      alert_(err instanceof ApiError ? err.message : t("files.too_large"));
    } finally {
      setUploading(false);
    }
  };

  const download = async (f: FileItem) => {
    try {
      await downloadExport(`/api/files/${f.id}`, f.name);
    } catch {
      alert_(t("download.fail"));
    }
  };

  const share = (f: FileItem) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    setShareSpec({
      key: `file:${f.id}`,
      title: f.name,
      withFormat: false, // uploaded files share in their native format
      url: () => `/api/files/${f.id}`,
      fallbackName: () => f.name,
      mime: () => f.content_type || "application/octet-stream",
    });
  };

  const open = async (f: FileItem) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    if (f.kind === "image") {
      try {
        const url = await fetchBlobUrl(`/api/files/${f.id}`);
        setPreview({ file: f, url });
      } catch {
        alert_(t("download.fail"));
      }
    } else {
      download(f);
    }
  };

  const del = (f: FileItem) => {
    const go = async () => {
      setBusyId(f.id);
      try {
        await api.delete(`/api/files/${f.id}`);
        load();
      } catch {
        alert_(t("common.error"));
      } finally {
        setBusyId(null);
      }
    };
    const c = tg()?.showConfirm;
    if (c) c(t("files.confirm_del_file"), (ok) => ok && go());
    else if (window.confirm(t("files.confirm_del_file"))) go();
  };

  return (
    <div className="page">
      <header className="doc-header">
        <button className="back-btn" onClick={() => navigate("/files")}>
          ‹
        </button>
        <h1 className="doc-title">📁 {t("files.title")}</h1>
      </header>

      <label className={"btn upload-btn" + (uploading ? " disabled" : "")}>
        {uploading ? t("files.uploading") : "⬆ " + t("files.upload")}
        <input type="file" hidden onChange={onPick} disabled={uploading} />
      </label>

      {error && <div className="alert error">{error}</div>}
      {!files && !error && (
        <div className="empty">
          <span className="spinner" />
        </div>
      )}
      {files && files.length === 0 && <div className="empty">{t("files.empty_files")}</div>}

      <div className="file-list">
        {files?.map((f) => (
          <div key={f.id} className="file-row">
            <span className="file-ico" onClick={() => open(f)}>
              {fileIcon(f)}
            </span>
            <div className="file-main" onClick={() => open(f)}>
              <div className="file-name">{f.name}</div>
              <div className="file-meta">{humanSize(f.size)}</div>
            </div>
            <button className="chip" onClick={() => share(f)} title={t("share.button")}>
              🔗
            </button>
            {isAdmin && (
              <button
                className="chip danger"
                disabled={busyId === f.id}
                onClick={() => del(f)}
                title={t("files.delete")}
              >
                🗑
              </button>
            )}
          </div>
        ))}
      </div>

      {preview && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="img-preview" onClick={(e) => e.stopPropagation()}>
            <img src={preview.url} alt={preview.file.name} />
            <div className="reader-actions">
              <button className="btn small" onClick={() => share(preview.file)}>
                🔗 {t("share.button")}
              </button>
              <button className="btn small" onClick={() => download(preview.file)}>
                ⬇ {t("files.download")}
              </button>
              <button className="btn secondary small" onClick={closePreview}>
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {shareSpec && <ShareSheet spec={shareSpec} onClose={() => setShareSpec(null)} />}
    </div>
  );
}
